import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiPost, useApiPut, useApiDelete, useApiMutation, useApiBlobMutation } from '../../hooks/use-api';
import {
  PlatformBlockListResponse,
  PlatformBlockDetailResponse,
  CreatePlatformBlockRequest,
  UpdatePlatformBlockRequest,
  ExerciseOption,
  EquipmentOption
} from './types';

// Query Keys
export const platformBlockKeys = {
  all: ['platform-blocks'],
  lists: () => [...platformBlockKeys.all, 'list'],
  list: (filters: Record<string, any>) => [...platformBlockKeys.lists(), JSON.stringify(filters)],
  details: () => [...platformBlockKeys.all, 'detail'],
  detail: (id: number) => [...platformBlockKeys.details(), id.toString()],
  categories: () => [...platformBlockKeys.all, 'categories'],
  scopes: () => [...platformBlockKeys.all, 'scopes'],
};

// Hooks for fetching data
export function usePlatformBlockList(
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    category?: number;
    difficulty?: number;
    equipmentId?: number;
    includeInactive?: boolean;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  } = {}
) {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.category) queryParams.append('category', params.category.toString());
  if (params.difficulty) queryParams.append('difficulty', params.difficulty.toString());
  if (params.equipmentId) queryParams.append('equipmentId', params.equipmentId.toString());
  if (params.includeInactive) queryParams.append('includeInactive', params.includeInactive.toString());
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);

  const endpoint = `/platform/blocks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  return useApiQuery<PlatformBlockListResponse>(
    platformBlockKeys.list(params),
    endpoint
  );
}

export function usePlatformBlockDetail(id: number | undefined) {
  return useApiQuery<PlatformBlockDetailResponse>(
    platformBlockKeys.detail(id || 0),
    `/platform/blocks/${id}`,
    {
      enabled: !!id && id > 0,
    }
  );
}

export function usePlatformBlockCategories() {
  return useApiQuery<Array<{ value: number; name: string }>>(
    platformBlockKeys.categories(),
    '/platform/blocks/categories'
  );
}

export function usePlatformBlockScopes() {
  return useApiQuery<Array<{ value: number; name: string }>>(
    platformBlockKeys.scopes(),
    '/platform/blocks/scopes'
  );
}

// Exercise options for block creation (reusing platform exercises)
export function usePlatformExerciseOptions() {
  return useApiQuery<{
    exercises: ExerciseOption[];
    pagination: { page: number; pageSize: number; totalCount: number; totalPages: number };
  }>(
    ['platform-exercises', 'options'],
    '/platform/exercises?pageSize=500&includeInactive=false'
  );
}

// Equipment options for dropdowns (reusing platform equipment)
export function usePlatformEquipmentOptions() {
  return useApiQuery<{
    equipment: EquipmentOption[];
    pagination: { page: number; pageSize: number; totalCount: number; totalPages: number };
  }>(
    ['platform-equipment', 'options'],
    '/platform/equipment?pageSize=100&includeInactive=false'
  );
}

// Hooks for mutations
export function useCreatePlatformBlock() {
  const queryClient = useQueryClient();

  return useApiPost<{ id: number; message: string }, any, CreatePlatformBlockRequest>(
    '/platform/blocks',
    {
      onSuccess: () => {
        // Invalidate and refetch block list
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.lists() });
      },
    }
  );
}

export function useUpdatePlatformBlock() {
  const queryClient = useQueryClient();

  return useApiPut<{ message: string }, any, UpdatePlatformBlockRequest & { id: number }>(
    (variables) => `/platform/blocks/${variables.id}`,
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch block list and specific item
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.detail(variables.id) });
      },
    }
  );
}

export function useDeletePlatformBlock() {
  const queryClient = useQueryClient();

  return useApiDelete<{ message: string }, any, { id: number }>(
    (variables) => `/platform/blocks/${variables.id}`,
    {
      onSuccess: () => {
        // Invalidate and refetch block list
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.lists() });
      },
    }
  );
}

export function useReactivatePlatformBlock() {
  const queryClient = useQueryClient();

  return useApiMutation<{ message: string }, any, { id: number }>(
    'POST',
    (variables) => `/platform/blocks/${variables.id}/reactivate`,
    {
      onSuccess: (data, variables) => {
        // Invalidate and refetch block list and specific item
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.lists() });
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.detail(variables.id) });
      },
    }
  );
}

export function useReorderPlatformBlocks() {
  const queryClient = useQueryClient();

  return useApiMutation<{ message: string }, any, { blockIds: number[] }>(
    'POST',
    () => '/platform/blocks/reorder',
    {
      onSuccess: () => {
        // Invalidate and refetch block list to reflect new order
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.lists() });
      },
    }
  );
}

// CSV Export/Import hooks
export function useExportPlatformBlocksCsv() {
  return useApiBlobMutation<any, {
    includeInactive?: boolean;
    search?: string;
    category?: number;
    difficulty?: number;
    primaryEquipmentId?: number;
  }>(
    'GET',
    (variables) => {
      const queryParams = new URLSearchParams();
      if (variables.includeInactive) queryParams.append('includeInactive', 'true');
      if (variables.search) queryParams.append('search', variables.search);
      if (variables.category) queryParams.append('category', variables.category.toString());
      if (variables.difficulty) queryParams.append('difficulty', variables.difficulty.toString());
      if (variables.primaryEquipmentId) queryParams.append('primaryEquipmentId', variables.primaryEquipmentId.toString());
      
      return `/platform/blocks/export/csv${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    }
  );
}

export function useImportPlatformBlocksCsv() {
  const queryClient = useQueryClient();

  return useApiMutation<
    { message: string; summary: { created: number; updated: number; skipped: number }; errors: string[] },
    any,
    { file: File }
  >(
    'POST',
    () => '/platform/blocks/import/csv',
    {
      onSuccess: () => {
        // Invalidate and refetch block list after import
        queryClient.invalidateQueries({ queryKey: platformBlockKeys.lists() });
      },
      requestType: 'multipart',
    }
  );
}